import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useApiBase } from './api-base-provider'
import { useWistorAuth } from '../../api/auth'
import { useV2Theme } from './v2-layout'

// ─── Icons as inline SVG paths ────────────────────────
const icons = {
  projects: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z',
  users: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
  sun: 'M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42',
  sunCenter: 'M12 16a4 4 0 100-8 4 4 0 000 8z',
  moon: 'M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z',
  menu: 'M3 12h18M3 6h18M3 18h18',
  chevron: 'M6 9l6 6 6-6',
}

function Icon({ d, size = 16, fill }: { d: string; size?: number; fill?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
         fill={fill ? 'currentColor' : 'none'}
         stroke={fill ? 'none' : 'currentColor'}
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

// ─── Wistor Logo SVG ──────────────────────────────────
function WistorLogo({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 1500 1500" fill="none">
      <path d="M843.55,371.17c-1.19-1.66-2.42-3.28-3.69-4.88-21.97-27.59-57.71-43.72-95.53-45.41-64.97,1.51-138.49-4.95-172.56,37.42l-87.23,112.43,214.9,273.21,211.88-274.65-67.77-98.11Z" fill="#1d1359"/>
      <path d="M864.46,529.64s-249.18-186.97-201.29-199.69c109.89-29.19,157.53,25.83,157.53,25.83l90.61,113.47-46.86,60.39Z" fill="url(#wlg1)"/>
      <path d="M771.46,1126.44c1.19,1.66,2.42,3.28,3.69,4.88,21.97,27.59,54.32,46.2,92.15,47.89,64.97-1.51,146.24,7.07,178.67-43.46l81.46-104.99-215.03-272.77-212.21,273.5,71.27,94.95Z" fill="#1d1359"/>
      <path d="M747.04,971.1s249.5,188.26,201.29,199.69c-93.3,22.12-146.3-8.87-146.3-8.87l-101.84-130.43,46.86-60.39Z" fill="url(#wlg2)"/>
      <path d="M780.53,1134.07c-1.28-1.59-2.5-3.22-3.69-4.88L373.78,609.54l-211.88,274.65,170.52,216.68c33.47,51.4,100.1,79.78,166.68,79.55l369.78-1.23c1.39,0,2.64-.08,4.16-.14-37.83-1.69-70.55-17.4-92.52-45Z" fill="#6950ff"/>
      <path d="M1273.23,373.08c-1.19-1.66-2.42-3.28-3.69-4.88-21.97-27.59-55.31-46.94-93.13-48.63-16.17,.38-32.87,.26-49.36,.53-4.75-.28-9.56-.45-14.52-.44l-369.78,1.23c-1.39,0-2.77,.11-4.17,.14,37.83,1.69,71.22,19.23,93.19,46.82,1.28,1.59,2.5,3.22,3.69,4.88l290.77,369.8,211.88-274.65-64.87-94.78Z" fill="#6950ff"/>
      <defs>
        <linearGradient id="wlg1" x1="700" y1="330" x2="860" y2="530" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#1d1359"/><stop offset="1" stopColor="#1d133b"/>
        </linearGradient>
        <linearGradient id="wlg2" x1="750" y1="970" x2="950" y2="1170" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#1d1359"/><stop offset="1" stopColor="#1d133b"/>
        </linearGradient>
      </defs>
    </svg>
  )
}

// ─── Nav items ────────────────────────────────────────
const NAV_ITEMS = [
  { path: '/v2/home', label: 'Projects', icon: icons.projects },
  { path: '/v2/users', label: 'Users', icon: icons.users },
]

// ─── Component ────────────────────────────────────────
export function V2GlobalHeader() {
  const { baseUrl, isRemote, savedTargets, setBaseUrl, addTarget, removeTarget } = useApiBase()
  const auth = useWistorAuth()
  const location = useLocation()
  const { mode, toggle: toggleTheme } = useV2Theme()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [apiDropdown, setApiDropdown] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [newUrl, setNewUrl] = useState('')

  const currentTarget = savedTargets.find(t => t.url === baseUrl)
  const displayLabel = currentTarget?.label || (baseUrl || 'Local')

  const handleAddTarget = () => {
    if (!newUrl.trim()) return
    const cleaned = newUrl.trim().replace(/\/+$/, '')
    addTarget({ label: newLabel.trim() || cleaned, url: cleaned })
    setBaseUrl(cleaned)
    setNewLabel('')
    setNewUrl('')
    setIsAdding(false)
    setApiDropdown(false)
  }

  const userInitial = auth.status === 'authorized'
    ? (auth.user.name || auth.user.email || '?')[0].toUpperCase()
    : '?'

  const isNavVisible = (item: typeof NAV_ITEMS[0]) => {
    if (item.path === '/v2/users' && auth.status === 'authorized') {
      return ['admin', 'superadmin'].includes(auth.user.role)
    }
    return true
  }

  return (
    <header className="v2-header" style={{ position: 'relative' }}>
      {/* Left: Logo + Nav */}
      <div className="v2-header-left">
        {/* Mobile hamburger */}
        <button
          className="v2-mobile-menu-btn"
          onClick={() => setMobileOpen(!mobileOpen)}
          type="button"
        >
          <Icon d={icons.menu} size={20} />
        </button>

        <Link to="/v2/home" className="v2-brand">
          <WistorLogo size={22} />
          <span className="v2-brand-text">
            wistor<span className="v2-brand-accent">.v2</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="v2-nav">
          {NAV_ITEMS.filter(isNavVisible).map(item => {
            const active = location.pathname.startsWith(item.path)
              || (item.path === '/v2/home' && location.pathname.startsWith('/v2/projects'))
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`v2-nav-item ${active ? 'active' : ''}`}
              >
                <Icon d={item.icon} size={14} />
                {item.label}
              </Link>
            )
          })}
        </nav>
      </div>

      {/* Right: API target + theme toggle + avatar */}
      <div className="v2-header-right">
        {/* API target pill */}
        <div style={{ position: 'relative' }}>
          <button
            className="v2-api-pill"
            onClick={() => setApiDropdown(!apiDropdown)}
            type="button"
          >
            <span className={`v2-api-dot ${isRemote ? 'v2-api-dot--remote' : 'v2-api-dot--local'}`} />
            <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayLabel}
            </span>
            <Icon d={icons.chevron} size={10} />
          </button>

          {apiDropdown && (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 200 }}
                onClick={() => { setApiDropdown(false); setIsAdding(false) }}
              />
              <div className="v2-more-dropdown" style={{ width: 320, right: 0, top: '100%', marginTop: 6 }}>
                <div style={{ padding: '8px 12px 4px', fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--v2-text-muted)' }}>
                  API Target
                </div>
                {savedTargets.map(target => (
                  <div key={target.url} style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="v2-more-item"
                      style={{
                        flex: 1,
                        background: target.url === baseUrl ? 'var(--v2-accent-soft)' : undefined,
                      }}
                      onClick={() => { setBaseUrl(target.url); setApiDropdown(false) }}
                    >
                      <span
                        className={`v2-api-dot ${target.url === '' ? 'v2-api-dot--local' : 'v2-api-dot--remote'}`}
                        style={{ width: 6, height: 6 }}
                      />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {target.label}
                        {target.url && (
                          <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.5 }}>
                            {target.url}
                          </span>
                        )}
                      </span>
                    </button>
                    {target.url !== '' && (
                      <button
                        type="button"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--v2-text-muted)', padding: '4px 8px', fontSize: 14,
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          removeTarget(target.url)
                          if (baseUrl === target.url) setBaseUrl('')
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}

                <div style={{ borderTop: '1px solid var(--v2-border)', margin: '4px 0' }} />

                {!isAdding ? (
                  <button
                    type="button"
                    className="v2-more-item"
                    style={{ color: 'var(--v2-accent-text)' }}
                    onClick={() => setIsAdding(true)}
                  >
                    + Add remote target
                  </button>
                ) : (
                  <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input
                      type="text"
                      placeholder="Label (e.g. Staging)"
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      className="v2-input"
                      style={{ fontSize: 12, padding: '6px 10px' }}
                    />
                    <input
                      type="text"
                      placeholder="https://deployed.com:3000"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      className="v2-input"
                      style={{ fontSize: 12, padding: '6px 10px' }}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTarget()}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="v2-btn v2-btn--primary v2-btn--sm"
                        onClick={handleAddTarget}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="v2-btn v2-btn--ghost v2-btn--sm"
                        onClick={() => { setIsAdding(false); setNewLabel(''); setNewUrl('') }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Theme toggle */}
        <button className="v2-theme-toggle" onClick={toggleTheme} type="button" title="Toggle theme">
          {mode === 'dark' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d={icons.sunCenter} />
              <path d={icons.sun} />
            </svg>
          ) : (
            <Icon d={icons.moon} size={16} />
          )}
        </button>

        {/* User avatar */}
        {auth.status === 'authorized' && (
          <div className="v2-avatar" title={`${auth.user.name} (${auth.user.role})`}>
            {userInitial}
          </div>
        )}
      </div>

      {/* Mobile nav overlay */}
      {mobileOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 90 }}
            onClick={() => setMobileOpen(false)}
          />
          <div className="v2-mobile-nav">
            {NAV_ITEMS.filter(isNavVisible).map(item => {
              const active = location.pathname.startsWith(item.path)
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`v2-mobile-nav-item ${active ? 'active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon d={item.icon} size={16} />
                  {item.label}
                </Link>
              )
            })}
          </div>
        </>
      )}
    </header>
  )
}